/**
 * FI Task Queue Page (Sprint 171 D-002)
 *
 * Admin-portal page rendering the FI task queue: pledges awaiting FI officer
 * or FI ops action. Composes @aaimplatform/ui primitives (FilterBar, DataGrid,
 * ActionBar, Dialog, MetricCard, Badge, Button) -- no ad-hoc components.
 *
 * Data is served by /api/admin/tasks via the useTasks / useTaskCount hooks.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  CheckIcon,
  CornerUpLeftIcon,
  UserPlusIcon,
} from 'lucide-react';
import {
  DataGrid,
  FilterBar,
  ActionBar,
  Dialog,
  Badge,
  Button,
  MetricCard,
  METRIC_READ_CAPTIONS,
  Skeleton,
  Textarea,
  Input,
  ShortcutHelpOverlay,
} from '@aaimplatform/ui';
import type {
  FilterDefinition,
  ActiveFilter,
  ColumnDef,
  SavedView,
  ActionDefinition,
} from '@aaimplatform/ui';
import {
  useTasks,
  useTaskCount,
  useBulkAcknowledgeTasks,
  useBulkReturnTasks,
  useReassignTask,
} from '../hooks/useTasks';
import type {
  Task,
  TaskAgeBand,
  PledgeStatus,
  TaskListQueryOptions,
} from '../api/services/tasks';
import {
  useKeyboardShortcuts,
  type KeyboardShortcutConfig,
} from '../hooks/useKeyboardShortcuts';
import { PledgeStatusBadge } from './pledges/pledge-display';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SAVED_VIEWS_STORAGE_KEY = 'aaim-171-task-queue-filters';
const DEFAULT_PAGE_LIMIT = 25;
/** Rows per grid page, the same as the other pledge lists (Inbox, Active Pledges). */
const GRID_PAGE_SIZE = 20;
/**
 * The package-ID search rides FilterBar's own search input, which renders its
 * placeholder as the accessible name. The '/' shortcut finds it by that name.
 */
const PACKAGE_ID_SEARCH_PLACEHOLDER = 'Search by package ID...';

/**
 * Minimum table width under the fixed layout. Between the md breakpoint and
 * this width, nine proportional columns were squeezed until their headers ran
 * together; the grid now scrolls sideways there instead (fi-g1-10).
 */
const TASK_GRID_MIN_WIDTH = 960;

/**
 * What an empty task grid says. The grid's default read "No data available"
 * and never said what was absent (fi-g1-5, fi-g1-11). A failed list read is not
 * an empty queue, and a filter or search that matches nothing is not one
 * either. Mirrors inboxEmptyMessage on the Pledge Inbox.
 */
export function tasksEmptyMessage({
  failed,
  narrowed,
}: {
  failed: boolean;
  narrowed: boolean;
}): string {
  if (failed) return 'Tasks could not be read, so none are listed.';
  if (narrowed) return 'No tasks match these filters or this search.';
  return 'No pledges are waiting on your action.';
}

/**
 * Tailwind `md`. Below it the grid drops `fixedLayout`: fixed layout turns
 * every column size into a percentage of the container, so nine columns were
 * squeezed into a 358 px phone viewport with colliding headers. Auto layout
 * with column resize keeps a minimum width and scrolls inside the grid.
 */
const FIXED_LAYOUT_MIN_WIDTH_QUERY = '(min-width: 768px)';

function useMediaMatch(query: string): boolean {
  const read = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : true;
  const [matches, setMatches] = useState<boolean>(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

// ---------------------------------------------------------------------------
// Filter definitions
// ---------------------------------------------------------------------------

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'PENDING', label: 'Pending' },
      { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
      { value: 'ACTIVE', label: 'Active' },
      { value: 'DECLINED', label: 'Declined' },
      { value: 'RETURNED', label: 'Returned' },
      { value: 'EXPIRED', label: 'Expired' },
      { value: 'WITHDRAWN', label: 'Withdrawn' },
      { value: 'RELEASED', label: 'Released' },
    ],
  },
  {
    id: 'ageBand',
    label: 'Age Band',
    type: 'select',
    options: [
      { value: 'green', label: 'Green (within SLA)' },
      { value: 'yellow', label: 'Yellow (approaching SLA)' },
      { value: 'red', label: 'Red (over SLA)' },
    ],
  },
  {
    id: 'assignedReviewer',
    label: 'Assigned Reviewer',
    type: 'text',
    placeholder: 'Reviewer id or email...',
  },
  {
    id: 'packageId',
    label: 'Package ID',
    type: 'text',
    placeholder: 'AAIM-2026-Q2-...',
  },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--';
  if (value < 1) return `${Math.round(value * 60)}m`;
  if (value < 24) return `${value.toFixed(1)}h`;
  const days = value / 24;
  return `${days.toFixed(1)}d`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Age-band badge -- maps to Badge variants (green->success, yellow->warning,
// red->error). Kept as a thin wrapper around the package primitive so no
// styling duplication is introduced.
// ---------------------------------------------------------------------------

function AgeBandBadge({ ageBand }: { ageBand: TaskAgeBand }) {
  const variant: 'success' | 'warning' | 'error' =
    ageBand === 'green' ? 'success' : ageBand === 'yellow' ? 'warning' : 'error';
  const label =
    ageBand === 'green'
      ? 'Within SLA'
      : ageBand === 'yellow'
        ? 'Approaching'
        : 'Over SLA';
  return (
    <Badge variant={variant} size="small">
      {label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Saved-filter persistence -- MVP: localStorage only; no cross-device sync.
// Key documented in mandate: `aaim-171-task-queue-filters`.
// ---------------------------------------------------------------------------

function readSavedViews(): SavedView[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(SAVED_VIEWS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SavedView[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeSavedViews(views: SavedView[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SAVED_VIEWS_STORAGE_KEY, JSON.stringify(views));
  } catch {
    // Storage quota or disabled -- silent fail. Views are non-essential.
  }
}

// ---------------------------------------------------------------------------
// TasksPage
// ---------------------------------------------------------------------------

export function TasksPage() {
  const navigate = useNavigate();

  // ---- Filter state --------------------------------------------------------

  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [savedViews, setSavedViews] = useState<SavedView[]>(() =>
    readSavedViews(),
  );

  useEffect(() => {
    writeSavedViews(savedViews);
  }, [savedViews]);

  const statusFilter = activeFilters.find((f) => f.filterId === 'status')
    ?.value as PledgeStatus | undefined;
  const ageBandFilter = activeFilters.find((f) => f.filterId === 'ageBand')
    ?.value as TaskAgeBand | undefined;
  const assignedReviewerFilter = activeFilters.find(
    (f) => f.filterId === 'assignedReviewer',
  )?.value;
  const packageIdFilter = activeFilters.find((f) => f.filterId === 'packageId')
    ?.value;

  // ---- Cursor pagination state -- accumulate pages client-side ------------

  // historicalPages holds items fetched from prior pagination steps. The
  // currently-visible page is appended from listData.items at render time so
  // we do not need a useEffect that depends on the query result reference
  // (which can change identity on every render under mocked conditions and
  // cause an infinite render loop).
  const [historicalPages, setHistoricalPages] = useState<Task[][]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const lastAppendedCursorRef = useRef<string | undefined>(undefined);

  // Reset accumulated pages when filters change.
  const filterSignature = useMemo(
    () =>
      JSON.stringify({
        statusFilter,
        ageBandFilter,
        assignedReviewerFilter,
        packageIdFilter,
      }),
    [statusFilter, ageBandFilter, assignedReviewerFilter, packageIdFilter],
  );

  useEffect(() => {
    setHistoricalPages([]);
    setCursor(undefined);
    lastAppendedCursorRef.current = undefined;
  }, [filterSignature]);

  const listParams: TaskListQueryOptions = useMemo(
    () => ({
      status: statusFilter,
      ageBand: ageBandFilter,
      assignedReviewer: assignedReviewerFilter,
      packageId: packageIdFilter,
      cursor,
      limit: DEFAULT_PAGE_LIMIT,
    }),
    [
      statusFilter,
      ageBandFilter,
      assignedReviewerFilter,
      packageIdFilter,
      cursor,
    ],
  );

  const {
    data: listData,
    isLoading: isListLoading,
    error: listError,
  } = useTasks(listParams);
  const {
    data: countData,
    isLoading: isCountLoading,
    isError: isCountError,
  } = useTaskCount();
  const wideViewport = useMediaMatch(FIXED_LAYOUT_MIN_WIDTH_QUERY);

  const currentItems: Task[] = listData?.items ?? [];
  const tasks: Task[] = useMemo(
    () => [...historicalPages.flat(), ...currentItems],
    [historicalPages, currentItems],
  );
  const nextCursor = listData?.nextCursor ?? null;

  // ---- Selection state -----------------------------------------------------

  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  // Clear selection on filter change.
  useEffect(() => {
    setSelectedTasks([]);
  }, [filterSignature]);

  // ---- Mutations -----------------------------------------------------------

  const ackMutation = useBulkAcknowledgeTasks();
  const returnMutation = useBulkReturnTasks();
  const reassignMutation = useReassignTask();

  // ---- Dialog state --------------------------------------------------------

  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('');

  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState<Task | null>(null);
  const [reassignReviewer, setReassignReviewer] = useState('');
  const [reassignReason, setReassignReason] = useState('');

  const [actionError, setActionError] = useState<string | null>(null);

  // ---- Keyboard-shortcut state --------------------------------------------
  //
  // focusedRowIndex is 0-based into the currently-rendered `tasks` array. It
  // tracks the row receiving the j/k navigation focus (distinct from the
  // checkbox selection state tracked in `selectedTasks`). -1 means no row is
  // focused -- j/k on an empty state will stay at -1 until data loads.
  const [focusedRowIndex, setFocusedRowIndex] = useState<number>(-1);
  const [helpOpen, setHelpOpen] = useState<boolean>(false);
  const [packageIdSearch, setPackageIdSearch] = useState<string>('');
  const pageContainerRef = useRef<HTMLDivElement | null>(null);

  // Keep the focused-row index inside the bounds of the current list. When
  // the list shrinks (filter change, post-action refetch) clamp the cursor.
  useEffect(() => {
    if (tasks.length === 0) {
      setFocusedRowIndex(-1);
      return;
    }
    setFocusedRowIndex((prev) => {
      if (prev < 0) return 0;
      if (prev >= tasks.length) return tasks.length - 1;
      return prev;
    });
  }, [tasks.length]);

  // Debounced-ish: on each keystroke in the package-id search, push a single
  // packageId filter into activeFilters so the list narrows. We keep the
  // input uncontrolled from activeFilters so the caret does not jump around
  // on filter reconciliation.
  useEffect(() => {
    const trimmed = packageIdSearch.trim();
    setActiveFilters((prev) => {
      const without = prev.filter((f) => f.filterId !== 'packageId');
      if (trimmed === '') return without;
      const next: ActiveFilter = {
        filterId: 'packageId',
        value: trimmed,
        label: 'Package ID',
        displayValue: trimmed,
      };
      return [...without, next];
    });
  }, [packageIdSearch]);

  // ---- Handlers ------------------------------------------------------------

  const handleLoadMore = useCallback(() => {
    if (!nextCursor) return;
    // Capture the items of the currently-visible page BEFORE advancing the
    // cursor. Using a ref guard prevents double-appending if the user clicks
    // twice before the next page has resolved.
    if (lastAppendedCursorRef.current !== cursor) {
      const snapshot = listData?.items ?? [];
      setHistoricalPages((prev) => [...prev, snapshot]);
      lastAppendedCursorRef.current = cursor;
    }
    setCursor(nextCursor);
  }, [nextCursor, cursor, listData]);

  const handleBulkAcknowledge = useCallback(async () => {
    setActionError(null);
    const pledgeIds = selectedTasks.map((t) => t.pledgeId);
    if (pledgeIds.length === 0) return;
    try {
      const result = await ackMutation.mutateAsync({ pledgeIds });
      if (result.failed.length > 0) {
        setActionError(
          `Acknowledged ${result.succeeded.length} of ${pledgeIds.length}. ` +
            `${result.failed.length} failed: ${result.failed[0]?.reason ?? 'unknown reason'}`,
        );
      }
      setSelectedTasks([]);
      setHistoricalPages([]); lastAppendedCursorRef.current = undefined;
      setCursor(undefined);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Bulk acknowledge failed',
      );
    }
  }, [ackMutation, selectedTasks]);

  const handleOpenReturnDialog = useCallback(() => {
    setReturnReason('');
    setActionError(null);
    setReturnOpen(true);
  }, []);

  const handleBulkReturn = useCallback(async () => {
    setActionError(null);
    const pledgeIds = selectedTasks.map((t) => t.pledgeId);
    if (pledgeIds.length === 0 || !returnReason.trim()) return;
    try {
      const result = await returnMutation.mutateAsync({
        pledgeIds,
        reason: returnReason.trim(),
      });
      if (result.failed.length > 0) {
        setActionError(
          `Returned ${result.succeeded.length} of ${pledgeIds.length}. ` +
            `${result.failed.length} failed: ${result.failed[0]?.reason ?? 'unknown reason'}`,
        );
      }
      setReturnOpen(false);
      setReturnReason('');
      setSelectedTasks([]);
      setHistoricalPages([]); lastAppendedCursorRef.current = undefined;
      setCursor(undefined);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Bulk return failed',
      );
    }
  }, [returnMutation, selectedTasks, returnReason]);

  const handleOpenReassignDialog = useCallback((task: Task) => {
    setReassignTarget(task);
    setReassignReviewer(task.assignedReviewer ?? '');
    setReassignReason('');
    setActionError(null);
    setReassignOpen(true);
  }, []);

  const handleReassign = useCallback(async () => {
    if (!reassignTarget || !reassignReviewer.trim()) return;
    setActionError(null);
    try {
      await reassignMutation.mutateAsync({
        pledgeId: reassignTarget.pledgeId,
        body: {
          newReviewerId: reassignReviewer.trim(),
          reason: reassignReason.trim() ? reassignReason.trim() : null,
        },
      });
      setReassignOpen(false);
      setReassignTarget(null);
      setReassignReviewer('');
      setReassignReason('');
      setHistoricalPages([]); lastAppendedCursorRef.current = undefined;
      setCursor(undefined);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Reassign failed',
      );
    }
  }, [reassignMutation, reassignTarget, reassignReviewer, reassignReason]);

  // The package-ID search box and its chip are one filter. Removing the chip
  // (or Clear all) empties the box, so the two never disagree.
  const handleFiltersChange = useCallback((next: ActiveFilter[]) => {
    if (!next.some((f) => f.filterId === 'packageId')) setPackageIdSearch('');
    setActiveFilters(next);
  }, []);

  // ---- SavedView callbacks -------------------------------------------------

  const handleSaveView = useCallback(
    (name: string, filters: ActiveFilter[]) => {
      const newView: SavedView = {
        id: `view-${Date.now()}`,
        name,
        filters,
      };
      setSavedViews((prev) => [...prev, newView]);
    },
    [],
  );

  const handleLoadView = useCallback((view: SavedView) => {
    setActiveFilters(view.filters);
  }, []);

  const handleDeleteView = useCallback((viewId: string) => {
    setSavedViews((prev) => prev.filter((v) => v.id !== viewId));
  }, []);

  // ---- Keyboard-shortcut handlers -----------------------------------------

  const toggleRowSelection = useCallback(
    (task: Task) => {
      setSelectedTasks((prev) => {
        const exists = prev.some((t) => t.pledgeId === task.pledgeId);
        if (exists) return prev.filter((t) => t.pledgeId !== task.pledgeId);
        return [...prev, task];
      });
    },
    [],
  );

  const focusPackageIdSearch = useCallback(() => {
    const input = pageContainerRef.current?.querySelector(
      `[role="toolbar"] input[aria-label="${PACKAGE_ID_SEARCH_PLACEHOLDER}"]`,
    );
    if (input instanceof HTMLInputElement) {
      input.focus();
      input.select();
    }
  }, []);

  const focusNextFilterControl = useCallback(() => {
    const container = pageContainerRef.current;
    if (!container) return;
    const toolbar = container.querySelector('[role="toolbar"]');
    if (!(toolbar instanceof HTMLElement)) return;
    const focusables = Array.from(
      toolbar.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (focusables.length === 0) return;
    const active = document.activeElement;
    const currentIndex = focusables.findIndex((el) => el === active);
    const next = focusables[(currentIndex + 1) % focusables.length];
    next?.focus();
  }, []);

  const closeAnyOpenOverlay = useCallback(() => {
    if (helpOpen) {
      setHelpOpen(false);
      return;
    }
    if (returnOpen) {
      setReturnOpen(false);
      return;
    }
    if (reassignOpen) {
      setReassignOpen(false);
    }
  }, [helpOpen, returnOpen, reassignOpen]);

  // ---- Shortcut map (must come AFTER handlers it references) --------------

  const shortcuts = useMemo<KeyboardShortcutConfig[]>(() => {
    const focusedTask: Task | undefined =
      focusedRowIndex >= 0 ? tasks[focusedRowIndex] : undefined;
    return [
      {
        keys: 'j',
        description: 'Move focus down one row',
        handler: () => {
          if (tasks.length === 0) return;
          setFocusedRowIndex((prev) =>
            prev < 0 ? 0 : Math.min(prev + 1, tasks.length - 1),
          );
        },
      },
      {
        keys: 'k',
        description: 'Move focus up one row',
        handler: () => {
          if (tasks.length === 0) return;
          setFocusedRowIndex((prev) => (prev <= 0 ? 0 : prev - 1));
        },
      },
      {
        keys: 'x',
        description: 'Toggle selection on the focused row',
        handler: () => {
          if (focusedTask) toggleRowSelection(focusedTask);
        },
      },
      {
        keys: ' ',
        description: 'Toggle selection on the focused row (space)',
        handler: () => {
          if (focusedTask) toggleRowSelection(focusedTask);
        },
      },
      {
        keys: 'a',
        description: 'Acknowledge the currently selected tasks',
        handler: () => {
          if (selectedTasks.length === 0) return;
          void handleBulkAcknowledge();
        },
      },
      {
        keys: 'r',
        description: 'Return the currently selected tasks (opens dialog)',
        handler: () => {
          if (selectedTasks.length === 0) return;
          handleOpenReturnDialog();
        },
      },
      {
        keys: 'e',
        description: 'Reassign the focused row (opens dialog)',
        handler: () => {
          if (focusedTask) handleOpenReassignDialog(focusedTask);
        },
      },
      {
        keys: '?',
        description: 'Open this keyboard shortcut help',
        handler: () => setHelpOpen(true),
      },
      {
        keys: 'Escape',
        description: 'Close any open dialog or help overlay',
        allowWhenModalOpen: true,
        handler: closeAnyOpenOverlay,
      },
      {
        keys: '/',
        description: 'Focus the package ID search',
        handler: focusPackageIdSearch,
      },
      {
        keys: 'g f',
        description: 'Cycle focus through filter controls',
        handler: focusNextFilterControl,
      },
    ];
  }, [
    tasks,
    focusedRowIndex,
    selectedTasks,
    toggleRowSelection,
    handleBulkAcknowledge,
    handleOpenReturnDialog,
    handleOpenReassignDialog,
    closeAnyOpenOverlay,
    focusPackageIdSearch,
    focusNextFilterControl,
  ]);

  useKeyboardShortcuts(shortcuts, { containerRef: pageContainerRef });

  // ---- Columns -------------------------------------------------------------

  const columns = useMemo<ColumnDef<Task, unknown>[]>(
    () => [
      {
        accessorKey: 'packageId',
        header: 'Package ID',
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void navigate(`/pledges/${row.original.pledgeId}`, {
                state: { from: { pathname: '/tasks', label: 'Back to Tasks' } },
              });
            }}
            className="max-w-full truncate text-sm font-mono text-accent hover:text-accent/80 hover:underline tabular-nums leading-normal text-left"
            title="View pledge detail"
          >
            {row.original.packageId}
          </button>
        ),
        size: 220,
      },
      {
        accessorKey: 'consumerName',
        header: 'Consumer',
        cell: ({ row }) => (
          <span className="block truncate text-sm font-sans text-primary leading-normal">
            {row.original.consumerName ?? '--'}
          </span>
        ),
        size: 200,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => <PledgeStatusBadge status={getValue() as string} />,
        size: 130,
      },
      {
        accessorKey: 'ageBand',
        header: 'SLA',
        cell: ({ row }) => <AgeBandBadge ageBand={row.original.ageBand} />,
        size: 130,
      },
      {
        accessorKey: 'ageHours',
        header: 'Age',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
            {formatHours(getValue() as number)}
          </span>
        ),
        size: 90,
      },
      {
        accessorKey: 'assignedReviewer',
        header: 'Assigned',
        cell: ({ getValue }) => (
          <span className="block truncate text-sm font-sans text-secondary leading-normal">
            {(getValue() as string | null | undefined) ?? 'Unassigned'}
          </span>
        ),
        size: 180,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ getValue }) => (
          <span className="block truncate text-sm font-sans text-secondary leading-normal">
            {formatDate(getValue() as string)}
          </span>
        ),
        size: 170,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant="secondary"
            size="sm"
            icon={UserPlusIcon}
            onClick={() => handleOpenReassignDialog(row.original)}
          >
            Reassign
          </Button>
        ),
        size: 130,
      },
    ],
    [navigate, handleOpenReassignDialog],
  );

  // ---- Bulk-action toolbar -------------------------------------------------

  const bulkActions: ActionDefinition[] = useMemo(
    () => [
      {
        id: 'acknowledge',
        label: 'Acknowledge',
        icon: CheckIcon,
        variant: 'primary',
        onClick: handleBulkAcknowledge,
        disabled: ackMutation.isPending || selectedTasks.length === 0,
      },
      {
        id: 'return',
        label: 'Return',
        icon: CornerUpLeftIcon,
        variant: 'secondary',
        onClick: handleOpenReturnDialog,
        disabled: returnMutation.isPending || selectedTasks.length === 0,
      },
    ],
    [
      handleBulkAcknowledge,
      handleOpenReturnDialog,
      ackMutation.isPending,
      returnMutation.isPending,
      selectedTasks.length,
    ],
  );

  // ---- Derived metrics -----------------------------------------------------

  // null means NOT READ -- never 0. The counts come from their own query, and
  // a count that failed or never ran used to default to 0 and print 'None
  // pending' / 'Within SLA' in green (the GH #2742 / #2917 withheld-read
  // class). Loading shows a skeleton; failed and unread print '--' with the
  // reason.
  const pendingCount: number | null =
    typeof countData?.pendingCount === 'number' ? countData.pendingCount : null;
  const overSlaCount: number | null =
    typeof countData?.overSlaCount === 'number' ? countData.overSlaCount : null;
  const countCaption: string | undefined = isCountError
    ? METRIC_READ_CAPTIONS.failed
    : countData
      ? pendingCount === null || overSlaCount === null
        ? METRIC_READ_CAPTIONS.notReported
        : undefined
      : METRIC_READ_CAPTIONS.unread;
  // The Visible tile counts rows the list read produced. A failed list read
  // produced none, and 'All loaded' over it is a claim nobody measured.
  const listCaption: string | undefined = listError
    ? METRIC_READ_CAPTIONS.failed
    : listData
      ? undefined
      : METRIC_READ_CAPTIONS.unread;

  // ---- Render --------------------------------------------------------------

  // Focus-ring for the j/k-focused row. Emitted as a scoped <style> tag so
  // we do NOT override DataGrid internals; the selector targets the row's
  // aria-rowindex (index + 2 in tanstack-table's convention) inside our
  // wrapper.
  const focusedAriaRowIndex =
    focusedRowIndex >= 0 ? focusedRowIndex + 2 : null;

  return (
    <div ref={pageContainerRef} tabIndex={-1} data-testid="task-queue-page">
      {focusedAriaRowIndex !== null && (
        <style>
          {`[data-testid="task-queue-page"] [role="row"][aria-rowindex="${focusedAriaRowIndex}"] > td { box-shadow: inset 2px 0 0 var(--color-accent, #D2001F); background-color: rgb(var(--color-accent) / 0.04); }`}
        </style>
      )}

      <div className="flex justify-end mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setHelpOpen(true)}
          aria-label="Show keyboard shortcuts"
        >
          Shortcuts (?)
        </Button>
      </div>

      {listError && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load tasks: {listError.message}
          </div>
        </div>
      )}

      {actionError && (
        <div className="bg-warning/5 border border-warning/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-warning flex-shrink-0" />
          <div className="text-sm font-sans text-warning">{actionError}</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {isCountLoading ? (
          Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-24" />)
        ) : (
          <>
            <MetricCard
              label="Pending Tasks"
              value={pendingCount === null ? '--' : pendingCount.toString()}
              caption={countCaption}
              change={
                pendingCount === null
                  ? undefined
                  : pendingCount > 0
                    ? 'Awaiting your action'
                    : 'None pending'
              }
              /* Zero pending is an absence, not a gain: neutral, never green. */
              changeType={pendingCount !== null && pendingCount > 0 ? 'negative' : 'neutral'}
            />
            <MetricCard
              label="Over SLA"
              value={overSlaCount === null ? '--' : overSlaCount.toString()}
              caption={countCaption}
              /*
                'Within SLA' over zero pending tasks asserts a posture about
                nothing (r3-fi-g1-7): with no task to time, the note says so.
              */
              change={
                overSlaCount === null
                  ? undefined
                  : overSlaCount > 0
                    ? 'Requires attention'
                    : pendingCount === 0
                      ? 'No tasks to time'
                      : 'Within SLA'
              }
              changeType={overSlaCount !== null && overSlaCount > 0 ? 'negative' : 'neutral'}
            />
          </>
        )}
        <MetricCard
          label="Visible"
          value={listCaption ? '--' : tasks.length.toString()}
          caption={listCaption}
          change={listCaption ? undefined : nextCursor ? 'More available' : 'All loaded'}
          changeType="neutral"
        />
      </div>

      <FilterBar
        filters={FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={handleFiltersChange}
        searchValue={packageIdSearch}
        onSearchChange={setPackageIdSearch}
        searchPlaceholder={PACKAGE_ID_SEARCH_PLACEHOLDER}
        savedViews={savedViews}
        onSaveView={handleSaveView}
        onLoadView={handleLoadView}
        onDeleteView={handleDeleteView}
        className="rounded-t-sm"
      />

      <ActionBar
        selectedCount={selectedTasks.length}
        totalCount={tasks.length}
        actions={bulkActions}
        onSelectAll={() => setSelectedTasks(tasks)}
        onDeselectAll={() => setSelectedTasks([])}
      />

      {isListLoading && tasks.length === 0 ? (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      ) : (
        <DataGrid<Task>
          data={tasks}
          columns={columns}
          enableRowSelection
          enableMultiRowSelection
          onSelectionChange={(rows: Task[]) => setSelectedTasks(rows)}
          enableColumnResize
          fixedLayout={wideViewport}
          minTableWidth={TASK_GRID_MIN_WIDTH}
          emptyMessage={tasksEmptyMessage({
            failed: Boolean(listError),
            narrowed: activeFilters.length > 0,
          })}
          defaultPageSize={GRID_PAGE_SIZE}
          hoverable
          striped
          getRowId={(row) => row.pledgeId}
          className="w-full rounded-b-sm shadow-sm"
        />
      )}

      {nextCursor && (
        <div className="flex justify-center mt-4">
          <Button
            variant="secondary"
            size="medium"
            onClick={handleLoadMore}
            disabled={isListLoading}
          >
            {isListLoading ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Return Dialog                                                      */}
      {/* ----------------------------------------------------------------- */}
      <Dialog
        open={returnOpen}
        onOpenChange={setReturnOpen}
        title={`Return ${selectedTasks.length} ${selectedTasks.length === 1 ? 'task' : 'tasks'}`}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              size="medium"
              onClick={() => setReturnOpen(false)}
              disabled={returnMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={() => void handleBulkReturn()}
              disabled={!returnReason.trim() || returnMutation.isPending}
            >
              {returnMutation.isPending ? 'Returning...' : 'Confirm return'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm font-sans text-secondary">
            The reason is recorded in the audit log for each pledge and surfaced
            to the consumer as the explanation for the returned pledge.
          </p>
          <Textarea
            label="Return reason"
            placeholder="Explain what needs revision..."
            value={returnReason}
            onChange={setReturnReason}
            required
            rows={4}
            maxLength={500}
            showCharCount
          />
        </div>
      </Dialog>

      {/* ----------------------------------------------------------------- */}
      {/* Reassign Dialog                                                    */}
      {/* ----------------------------------------------------------------- */}
      <Dialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        title={
          reassignTarget
            ? `Reassign ${reassignTarget.packageId}`
            : 'Reassign task'
        }
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              size="medium"
              onClick={() => setReassignOpen(false)}
              disabled={reassignMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={() => void handleReassign()}
              disabled={
                !reassignReviewer.trim() || reassignMutation.isPending
              }
            >
              {reassignMutation.isPending ? 'Reassigning...' : 'Confirm reassign'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="New reviewer"
            placeholder="Reviewer user id"
            value={reassignReviewer}
            onChange={(e) => setReassignReviewer(e.target.value)}
            required
          />
          <Input
            label="Reason (optional)"
            placeholder="Why this reviewer is being reassigned..."
            value={reassignReason}
            onChange={(e) => setReassignReason(e.target.value)}
          />
        </div>
      </Dialog>

      {/* ----------------------------------------------------------------- */}
      {/* Keyboard Shortcut Help                                             */}
      {/* ----------------------------------------------------------------- */}
      <ShortcutHelpOverlay
        open={helpOpen}
        onOpenChange={setHelpOpen}
        shortcuts={shortcuts}
      />
    </div>
  );
}
